import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import './theme.css';

// Components
import { LandingPage, Login, RedactionApp, PrivacyPolicy, Terms, Support, Contact, Dashboard, Pricing, UserAgreement, Security } from './components';
// New Privacy Policy Page
// Utilities
import { 
  checkAuth as apiCheckAuth, 
  logoutUser
} from './utils/apiUtils';

function App() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [theme, setTheme] = useState('dark');
  const navigate = useNavigate();
  const location = useLocation();

  const checkAuthentication = useCallback(async () => {
    console.log('🔐 Checking authentication...');
    try {
      const data = await apiCheckAuth();
      console.log('🔐 Auth check result:', data);
      if (data.login) {
        console.log('✅ User is authenticated:', data.login);
        setUser(data);
      } else {
        console.log('❌ User is not authenticated');
        setUser(null);
      }
      return data;
    } catch (error) {
      console.error('🔐 Error checking authentication:', error);
      setUser(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check authentication on mount
  useEffect(() => {
    console.log('🚀 App.jsx useEffect - Starting authentication check...');
    checkAuthentication();
    const storedTheme = localStorage.getItem('redacto-theme');
    if (storedTheme === 'light' || storedTheme === 'dark') {
      setTheme(storedTheme);
    }

    // Listen for popup authentication messages
    const handleAuthMessage = (event) => {
      if (event.data && event.data.type === 'GOOGLE_AUTH_SUCCESS') {
        console.log('🎉 Popup authentication successful!', event.data.user);
        checkAuthentication();
        navigate('/app', { replace: true });
      }
    };
    
    window.addEventListener('message', handleAuthMessage);
    
    return () => {
      window.removeEventListener('message', handleAuthMessage);
    };
  }, [checkAuthentication, navigate]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const authStatus = urlParams.get('auth');

    if (authStatus === 'drive_success') {
      console.log('🎉 OAuth authentication successful!');
      const finalizeAuthentication = async () => {
        const data = await checkAuthentication();
        if (data?.login) {
          navigate('/app', { replace: true });
        } else {
          navigate('/login', { replace: true });
        }
      };

      finalizeAuthentication();
      return;
    }

    if (authStatus === 'error') {
      const errorMessage = urlParams.get('message') || 'Authentication failed';
      console.error('❌ OAuth authentication failed:', errorMessage);
      navigate('/login', { replace: true });
    }
  }, [checkAuthentication, location.search, navigate]);

  useEffect(() => {
    document.body.classList.remove('theme-dark', 'theme-light');
    document.body.classList.add(`theme-${theme}`);
    localStorage.setItem('redacto-theme', theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((currentTheme) => (currentTheme === 'dark' ? 'light' : 'dark'));
  }, []);

  const handleLogout = async () => {
    try {
      await logoutUser();
      setUser(null);
      // Navigate to landing page after logout
      navigate('/');
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  const handleGoogleSuccess = async (userData) => {
    // Store the complete user data instead of just the username
    // userData contains {success: true, user: {...}}, so we need userData.user
    const userToSet = userData.user || userData;
    setUser(userToSet);
  };

  // Show loading screen while checking authentication
  if (isLoading) {
    return (
      <div className="container" style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#4285f4'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <div className="container">
      <Routes>
        {/* Landing page route - accessible to everyone */}
        <Route 
          path="/" 
          element={
            <LandingPage user={user} theme={theme} toggleTheme={toggleTheme} />
          } 
        />
        <Route
          path="/login"
          element={
            user ? (
              <Navigate to="/app" replace />
            ) : (
              <Login
                isLoading={isLoading}
                onGoogleSuccess={handleGoogleSuccess}
                user={user}
              />
            )
          }
        />
        {/* Redaction app */}
        <Route 
          path="/app" 
          element={
            <RedactionApp 
              user={user} 
              onLogout={handleLogout}
              files={files}
              setFiles={setFiles}
              theme={theme}
              toggleTheme={toggleTheme}
            />
          } 
        />
        <Route
          path="/dashboard"
          element={<Dashboard />}
        />
        {/* Privacy Policy page */}
        <Route 
          path="/privacy-policy" 
          element={<PrivacyPolicy theme={theme} toggleTheme={toggleTheme} />} 
        />
        <Route path="/security" element={<Security theme={theme} toggleTheme={toggleTheme} />} />
        <Route path="/terms" element={<Terms theme={theme} toggleTheme={toggleTheme} />} />
        <Route path="/user-agreement" element={<UserAgreement theme={theme} toggleTheme={toggleTheme} />} />
        <Route path="/support" element={<Support theme={theme} toggleTheme={toggleTheme} />} />
        <Route path="/contact" element={<Contact theme={theme} toggleTheme={toggleTheme} />} />
        <Route path="/pricing" element={<Pricing />} />
        
     {/* Redirect any unknown routes to landing page */}
        <Route 
          path="*" 
          element={
            <LandingPage user={user} theme={theme} toggleTheme={toggleTheme} />
          } 
        />
      </Routes>
    </div>
  );
}

export default App;
